import { FC, useCallback } from "react";
import { Brand, Button, Division, Flex, Icon, Icons, Menu, MenuItemProps, Span, useToast } from "@ir/ui";
import { useUser } from "@/hooks";
import { useWorkspace } from "@/hooks/workspace";
import "./styles/sidebar.brands.scss";
import { GetUserDetailsUseCase } from "@/core/usecases/auth";
import { AuthRepository } from "@/infrastructure/repositories/auth";

const brandMenuClassNme = "sidebar-content-brandMenu";

const SidebarBrandsMenu: FC<{
  menuOpenChange: (isOpen: boolean) => void;
}> = ({ menuOpenChange }) => {
  const { userBrands } = useUser();
  const { workspace } = useWorkspace();
  const { error: errorToast } = useToast();

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (userBrands && userBrands.length > 1) {
        menuOpenChange(isOpen);
      }
    },
    [menuOpenChange, userBrands]
  );

  const handleBrandSelect = useCallback((brandKey: string) => {
    new GetUserDetailsUseCase(new AuthRepository())
      .execute(brandKey)
      .then(result => {
        if (result.type === "success") {
          globalThis.location.href = "/";
        } else {
          errorToast({ content: result.errorMessage });
        }
      })
      .catch(() => {
        errorToast({ content: "Something went wrong" });
      });
  }, [errorToast]);

  if (!userBrands || userBrands.length === 0) {
    return null;
  }

  const brandMenuItems: MenuItemProps[] = userBrands.map(brand => ({
    key: brand.brandKey,
    suffix: (
      <Flex align="center">
        <Brand boxShadow brandName={brand.brandName} logoSrcCompact={brand.brandLogo ?? ""} variant="compact" size="small" />
        <Flex direction="column" className="ms-2">
          <Span className="sidebar-menu-brand-name font-bold text-md">{brand.brandName}</Span>
        </Flex>
      </Flex>
    ),
    disabled: brand.brandKey === workspace?.workspaceKey,
  }));

  const renderBrandMenu = () => (
    <Button variant="clear" color="primary" className="slide-navigation-item">
      <Brand
        className="slide-navigation-icon"
        brandName={workspace?.workspaceName}
        logoSrcCompact={workspace?.workspaceLogo ?? ""}
        variant="compact"
        size="small"
        boxShadow
      />
      <Flex direction="column" className="slide-navigation-label">
        <Span className="sidebar-brand-name font-bold text-md text-grey-600">{workspace?.workspaceName}</Span>
        <Span className="sidebar-org-name text-xs text-grey-500">{workspace?.organizationName}</Span>
      </Flex>
      {userBrands.length > 1 && <Icon slot="end" name={Icons.ChevronDown} size="small" color="grey" />}
    </Button>
  );

  return (
    <Division className={brandMenuClassNme}>
      {userBrands.length > 1 ? (
        <Menu className="brand-menu-items" items={brandMenuItems} placement="top start" onOpenChange={handleOpenChange} onSelect={handleBrandSelect}>
          {renderBrandMenu()}
        </Menu>
      ) : (
        renderBrandMenu()
      )}
    </Division>
  );
};

export default SidebarBrandsMenu;
